import type { AddressInput } from "gql/generated";
import { EnumAddressLocationType } from "gql/generated";
import type { GoogleMapsAddressDetails } from "shared/hooks";

export type AddressSearchResponseValue = {
  fullAddress: string;
  line1?: string;
  line2?: string;
  city?: string;
  subdivision?: string;
  postalCode?: string;
  country?: string;
  placeId?: string;
  source?: "google" | "manual";
  location?: {
    type: "Point";
    coordinates: [number, number];
  };
};

export function parseAddressSearchResponse(
  response: Array<string | null | undefined>,
): AddressSearchResponseValue | null {
  const json = response[1];
  if (json) {
    try {
      const parsed = JSON.parse(json) as AddressSearchResponseValue;
      if (parsed && typeof parsed.fullAddress === "string") {
        return parsed;
      }
    } catch {
      // Fall back to the display value below.
    }
  }

  const display = response[0];
  return display ? { fullAddress: display } : null;
}

export function formatAddressSearchResponse(
  response: Array<string | null | undefined>,
): string {
  return parseAddressSearchResponse(response)?.fullAddress || "—";
}

export function serializeAddressSearchResponse(
  value: AddressSearchResponseValue,
): string[] {
  return [value.fullAddress, JSON.stringify(value)];
}

export function addressSearchValueFromGoogleAddress(
  address: GoogleMapsAddressDetails,
): AddressSearchResponseValue {
  return {
    placeId: address.placeId,
    fullAddress: address.fullAddress,
    line1: address.line1,
    line2: address.line2,
    city: address.city,
    subdivision: address.subdivision,
    postalCode: address.postalCode,
    country: address.country,
    source: "google",
    location: {
      type: "Point",
      coordinates: [
        address.location.coordinates[0] ?? 0,
        address.location.coordinates[1] ?? 0,
      ],
    },
  };
}

export function isAddressSearchValueComplete(
  value: AddressSearchResponseValue | null | undefined,
): boolean {
  return !!value?.fullAddress?.trim();
}

export function addressSearchValueToAddressInput(
  value: AddressSearchResponseValue,
): AddressInput {
  return {
    line1: value.line1,
    line2: value.line2,
    city: value.city,
    subdivision: value.subdivision,
    postalCode: value.postalCode,
    country: value.country,
    ...(value.location?.coordinates?.length
      ? {
          location: {
            type: EnumAddressLocationType.Point,
            coordinates: value.location.coordinates,
          },
        }
      : {}),
  };
}

type GraphqlAddressLike = {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  subdivision?: string | null;
  postalCode?: string | null;
  country?: string | null;
  location?: {
    type?: string | null;
    coordinates?: Array<number | null> | null;
  } | null;
} | null | undefined;

export function addressToAddressSearchValue(
  address: GraphqlAddressLike,
): AddressSearchResponseValue | null {
  if (!address) return null;

  const fullAddress = [
    address.line1,
    address.line2,
    address.city,
    address.subdivision,
    address.postalCode,
    address.country,
  ]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(", ");

  if (!fullAddress) return null;

  const lng = address.location?.coordinates?.[0];
  const lat = address.location?.coordinates?.[1];

  return {
    line1: address.line1 ?? undefined,
    line2: address.line2 ?? undefined,
    city: address.city ?? undefined,
    subdivision: address.subdivision ?? undefined,
    postalCode: address.postalCode ?? undefined,
    country: address.country ?? undefined,
    fullAddress,
    source:
      lng != null && lat != null && (address.line1 || address.city)
        ? "google"
        : "manual",
    ...(lng != null && lat != null
      ? {
          location: {
            type: "Point" as const,
            coordinates: [lng, lat] as [number, number],
          },
        }
      : {}),
  };
}

export function parseCartSnapshotCustomerAddress(
  value: unknown,
): AddressSearchResponseValue | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as AddressSearchResponseValue;
  return isAddressSearchValueComplete(candidate) ? candidate : null;
}
